import React, { useState, useRef, useEffect } from "react";
import { Send, Timer, Users, Eraser, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePoll } from "@/hooks/usePoll";
import { AuraRing } from "@/components/brand/AuraRing";
import { MentionInput } from "@/components/common/MentionInput";
import { api, mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

// Shared live chat (polled). Hosts/staff get slow mode, followers-only and clear chat.
export function LiveChat({ broadcast, canModerate, onBroadcast, className = "" }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const lastRef = useRef(null);
  const boxRef = useRef(null);
  const live = broadcast.status === "live";

  usePoll(async () => {
    try {
      const rows = await api.liveChat(broadcast.id, lastRef.current);
      if (!rows.length) return;
      lastRef.current = rows[rows.length - 1].created_at;
      setMsgs((cur) => [...cur, ...rows.filter((r) => !cur.some((c) => c.id === r.id))].slice(-200));
    } catch {}
  }, live ? 2500 : 60000, [broadcast.id]);

  useEffect(() => { boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight, behavior: "smooth" }); }, [msgs.length]);

  const send = async () => {
    const t = text.trim(); if (!t || sending) return;
    setSending(true);
    try { const m = await api.liveChatSend(broadcast.id, t); setMsgs((c) => [...c, m]); setText(""); lastRef.current = m.created_at; }
    catch (e) { toast(e.message); } finally { setSending(false); }
  };
  const setting = async (body) => { try { onBroadcast && onBroadcast(await api.liveChatSettings(broadcast.id, body)); } catch (e) { toast(e.message); } };
  const clear = async () => { if (!window.confirm("Clear the chat for everyone?")) return; try { await api.liveChatClear(broadcast.id); setMsgs([]); toast("Chat cleared"); } catch (e) { toast(e.message); } };
  const slow = broadcast.chat?.slow_mode_seconds || 0;

  return (
    <div data-testid="live-chat" className={cn("flex min-h-0 flex-col rounded-2xl border border-am-border bg-am-surface", className)}>
      <div className="flex items-center gap-2 border-b border-am-border px-3 py-2 text-xs font-semibold text-am-text2">
        Live chat
        {slow > 0 && <span className="rounded-full bg-white/5 px-2 py-0.5">Slow {slow}s</span>}
        {broadcast.chat?.followers_only && <span className="rounded-full bg-white/5 px-2 py-0.5">Followers only</span>}
        {canModerate && live && (
          <span className="ml-auto flex items-center gap-1">
            <button onClick={() => setting({ slow_mode_seconds: slow ? 0 : 10 })} data-testid="chat-slow-toggle" title="Slow mode" className={cn("grid h-7 w-7 place-items-center rounded-full hover:bg-white/10", slow && "text-aura-cyan")}><Timer className="h-3.5 w-3.5" /></button>
            <button onClick={() => setting({ followers_only: !broadcast.chat?.followers_only })} data-testid="chat-followers-toggle" title="Followers-only" className={cn("grid h-7 w-7 place-items-center rounded-full hover:bg-white/10", broadcast.chat?.followers_only && "text-aura-cyan")}><Users className="h-3.5 w-3.5" /></button>
            <button onClick={clear} data-testid="chat-clear" title="Clear chat" className="grid h-7 w-7 place-items-center rounded-full hover:bg-white/10 hover:text-aura-magenta"><Eraser className="h-3.5 w-3.5" /></button>
          </span>
        )}
      </div>
      <div ref={boxRef} className="flex-1 space-y-2 overflow-y-auto p-3">
        {msgs.length === 0 && <p className="py-6 text-center text-xs text-am-text3">{live ? "Say hi to the founders." : "Chat has ended."}</p>}
        {msgs.map((m) => (
          <div key={m.id} data-testid="live-chat-msg" className="flex items-start gap-2 text-sm">
            <AuraRing src={mediaUrl(m.author?.picture) || undefined} size={24} ring={1.5} fallback={m.author?.name} />
            <p className="min-w-0 flex-1 leading-snug"><span className={cn("mr-1.5 font-semibold", m.is_host ? "text-aura-gold" : "text-white")}>{m.author?.name}{m.is_host && <span className="ml-1 rounded bg-aura-gold/15 px-1 text-[9px] font-black uppercase text-aura-gold">Host</span>}</span><span className="text-am-text">{m.text}</span></p>
          </div>
        ))}
      </div>
      {live && (
        <div className="flex items-center gap-2 border-t border-am-border p-2">
          <MentionInput value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="live-chat-input" placeholder="Chat… use @ to mention" maxLength={300} className="rounded-full border border-am-border bg-am-surface2 px-4 py-2 text-sm text-white outline-none focus:border-am-border-strong" />
          <button onClick={send} disabled={sending} data-testid="live-chat-send" className="grid h-9 w-9 shrink-0 place-items-center rounded-full aura-gradient text-black disabled:opacity-50">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
        </div>
      )}
    </div>
  );
}
